var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Table_1;
import React, { Component, createRef } from 'react';
import { Cell, Column, Table as DataTable, HeaderCell } from 'rsuite-table';
import { SelectionType, SortOrder, TextAlignment, Unit } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { CollapseEvent, SelectItemEvent, SortEvent, UnselectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { EditorService, ExperimentalFlagsService, MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, difference, isDefined, isFunction, isMobile, noop } from '@philips-internal/oswald-ui-utils';
import { ActionStyle } from '../../action/action-constants.js';
import { CollapseArrow } from '../../collapse-arrow/collapse-arrow.js';
import { HeadingContext } from '../../heading/components/heading-context/heading-context.js';
import { Loader } from '../../loader/loader.js';
import { NotFound } from '../../not-found/not-found.js';
import { TooltipTrigger } from '../../tooltip/tooltip-constants.js';
import { Tooltip } from '../../tooltip/tooltip.js';
import { TableCheckbox } from '../components/table-checkbox.js';
import { TableCustomizeModal } from '../components/table-customize-modal.js';
import { getCheckboxColumn, getDefaultColumn, getDynamicColumn, getSuperscriptionBarColumn, getSuperscriptionColumn } from '../components/table-rows.js';
import { ColumnType, DEFAULT_ACTION_COLUMN_WIDTH, DEFAULT_CHECKBOX_COLUMN_WIDTH, DEFAULT_SUPERSCRIPTION_COLUMN_WIDTH, DEFAULT_TABLE_COLUMN_WIDTH, DEFAULT_TREE_GROUP_LABEL_COLUMN_WIDTH, RSTABLE_DEFAULT_COLUMN_SIZE } from '../table-column/table-column-constants.js';
import { TableProvider } from '../table-context.js';
import { findRowByKey, getDynamicContent, getTableColumnWidth } from '../table-utils.js';
import TableAPI from './table-api.js';
import 'rsuite-table/dist/css/rsuite-table.css';
import './table.css';
const TableContainer = {
    acceptedTypes: [
        ComponentType.LIST,
    ],
    name: '',
};
const LOADING_MESSAGE = 'tws-table.loading';
const CUSTOMIZE_COLUMN_MESSAGE = 'tws-table.customize';
const supportedSelectionTypes = [
    SelectionType.MULTIPLE,
    SelectionType.NONE,
    SelectionType.SINGLE,
];
const scrollbarWidth = 4;
let Table = Table_1 = class Table extends Component {
    constructor() {
        super(...arguments);
        this.providerRef = createRef();
        this.tableRef = createRef();
        this.popClassName = noop;
        this.state = {
            columnGroups: {
                barColumns: [],
                defaultColumns: [],
            },
            customizing: false,
            expandedRows: [],
            selectedRows: [],
        };
        this.onCheckboxRowChange = (data) => {
            const { keyField } = this.props;
            const { selectedRows } = this.state;
            const key = String(data[keyField]);
            let newSelectedRows;
            if (selectedRows.includes(key)) {
                newSelectedRows = [...selectedRows].filter((row) => row !== key);
            }
            else {
                newSelectedRows = [...selectedRows, key];
            }
            this.setState({ selectedRows: newSelectedRows });
        };
        this.onCheckboxColumnChange = () => {
            const { data, keyField } = this.props;
            const { selectedRows } = this.state;
            const newSelectedRows = selectedRows.length >= 0 && selectedRows.length != data.length
                ? data.map((item) => String(item[keyField]))
                : [];
            this.setState({ selectedRows: newSelectedRows });
        };
        this.onCloseCustomizeColumn = () => {
            this.setState({ customizing: false });
        };
        this.onColumnSort = (event) => {
            const { name } = event;
            const { onSort } = this.props;
            const fn = () => {
                const { name = '', direction = '' } = this.state.sortedColumn || {};
                if (isFunction(onSort)) {
                    onSort(new SortEvent({ direction, name }));
                }
            };
            this.setState((prevState) => {
                const { sortedColumn } = prevState;
                let direction = SortOrder.DESC;
                if (sortedColumn && sortedColumn.name === name) {
                    direction = sortedColumn.direction === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC;
                }
                return {
                    sortedColumn: { direction, name },
                };
            }, fn);
        };
        this.onCustomizeColumn = () => {
            this.setState(({ customizing }) => ({ customizing: !customizing }));
        };
        this.onDynamicContentRender = (event) => {
            const { onDynamicContentRender } = this.props;
            if (isFunction(onDynamicContentRender)) {
                onDynamicContentRender(event);
            }
        };
        this.onRenderExpandedContent = (data) => {
            if (!data) {
                return null;
            }
            const content = getDynamicContent(data, '', 'row', null, this.onDynamicContentRender);
            return (React.createElement("div", null, content));
        };
        this.onRowClick = (data) => {
            const { keyField, selectionType } = this.props;
            const { selectedRows } = this.state;
            if (selectionType !== SelectionType.SINGLE) {
                return;
            }
            const key = String(data[keyField]);
            this.setState({
                selectedRows: selectedRows.includes(key) ? [] : [key],
            });
        };
        this.onRowExpandable = (data) => {
            const { initialExpanded, keyField, onCollapse } = this.props;
            const { expandedRows } = this.state;
            const key = String(data[keyField]);
            const expanded = expandedRows.includes(key) ? true : Boolean(initialExpanded);
            const newExpandedRows = expanded
                ? expandedRows.filter((row) => (row !== key))
                : [...expandedRows, key];
            this.setState({ expandedRows: newExpandedRows });
            if (isFunction(onCollapse)) {
                onCollapse(new CollapseEvent({ collapsed: expanded }));
            }
        };
        this.onSelectItem = (data) => {
            const { onSelectItem } = this.props;
            if (isFunction(onSelectItem)) {
                onSelectItem(new SelectItemEvent({ item: data }));
            }
        };
        this.onSelectCustomize = (event) => {
            const { defaultColumns } = this.state.columnGroups;
            const columns = event.value;
            const newDefaultColumns = defaultColumns.map((defaultColumn) => ({
                ...defaultColumn,
                visible: columns.some((column) => column.id === defaultColumn.uuid) || undefined,
            }));
            this.setState(({ columnGroups }) => ({ columnGroups: { barColumns: columnGroups.barColumns, defaultColumns: newDefaultColumns } }));
        };
        this.onSort = (items) => {
            const sortedItems = items;
            const { defaultColumns } = this.state.columnGroups;
            const sortedColumns = sortedItems
                .map((item) => defaultColumns.find((column) => column.uuid === item.id))
                .filter((column) => isDefined(column));
            this.setState((previousState) => ({
                columnGroups: {
                    barColumns: previousState.columnGroups.barColumns,
                    defaultColumns: sortedColumns,
                },
            }));
        };
        this.onResize = (dataRow, columnWidth) => {
            const { current } = this.providerRef;
            const column = dataRow;
            column.width = columnWidth || RSTABLE_DEFAULT_COLUMN_SIZE;
            current?.addOrUpdateColumn(column);
        };
        this.onUnselectItem = (data) => {
            const { onUnselectItem } = this.props;
            if (onUnselectItem && isFunction(onUnselectItem)) {
                onUnselectItem(new UnselectItemEvent({ item: data }));
            }
        };
        this.getRowClassName = (rowData) => {
            const { keyField } = this.props;
            const { selectedRows } = this.state;
            if (!rowData) {
                return 'c-table__header-row';
            }
            const selected = selectedRows.includes(String(rowData[keyField]));
            const rowClass = classNames({
                'c-table__row': true,
                'c-table__row-select': selected,
            });
            return rowClass;
        };
    }
    componentDidMount() {
        if (!this.context) {
            return;
        }
        this.addContextualBarActions();
        this.popClassName = this.context.pushCustomClassName('c-table__contextual-bar');
    }
    componentDidUpdate(_, prevState) {
        const { selectedRows: prevSelectedRows } = prevState;
        const { selectedRows } = this.state;
        const tableProvider = this.providerRef.current;
        this.fireSelectionRelatedEvent(prevSelectedRows, selectedRows, this.onUnselectItem);
        this.fireSelectionRelatedEvent(selectedRows, prevSelectedRows, this.onSelectItem);
        if (this.unlistenColumns || !tableProvider) {
            return;
        }
        this.unlistenColumns = tableProvider.addColumnListener(() => this.updateColumns(this.getColumns()));
        this.updateColumns(this.getColumns());
    }
    componentWillUnmount() {
        if (isFunction(this.unlistenColumns)) {
            this.unlistenColumns();
        }
        this.popClassName();
    }
    addContextualBarActions() {
        const { customizeColumnsExpression, enableColumnCustomization } = this.props;
        if (enableColumnCustomization) {
            this.context?.addOrUpdateAction({
                enabled: true,
                id: 'customize-columns',
                onClick: this.onCustomizeColumn,
                style: ActionStyle.TEXT,
                text: customizeColumnsExpression,
            });
        }
    }
    isHiddenColumn(columnProps) {
        const { hiddenColumns = Table_1.defaultProps.hiddenColumns } = this.props;
        return hiddenColumns.includes(columnProps.entry);
    }
    getColumns() {
        const { current } = this.providerRef;
        return current?.getColumns() ?? [];
    }
    updateColumns(columns) {
        const barColumns = columns.filter((column) => column.type === ColumnType.SUPERSCRIPTION_BAR);
        const defaultColumns = difference(columns, barColumns);
        this.setState({ columnGroups: { barColumns, defaultColumns } });
    }
    fireSelectionRelatedEvent(source, target, event) {
        const { data, keyField } = this.props;
        difference(source, target).forEach((key) => {
            const row = findRowByKey(data, key, keyField);
            if (row) {
                event(row);
            }
        });
    }
    getClassName() {
        const { className } = this.props;
        const isTree = ExperimentalFlagsService.getEnableTreeTable() && Boolean(this.props.isTree);
        return classNames({
            'c-table': true,
            'c-tree-table': isTree,
            'u-scrollbar': true,
            [`${className}`]: Boolean(className),
        });
    }
    getTableWidth() {
        const { isTree, selectionType, withActions, withExpandedIndicator } = this.props;
        let width = this.getColumns().reduce((accumaltor, column) => {
            return accumaltor += this.getColumnWidth(column);
        }, 0);
        if (selectionType === SelectionType.MULTIPLE) {
            width += DEFAULT_TABLE_COLUMN_WIDTH;
        }
        if (withActions) {
            width += DEFAULT_ACTION_COLUMN_WIDTH;
        }
        if (withExpandedIndicator) {
            width += DEFAULT_TABLE_COLUMN_WIDTH;
        }
        if (ExperimentalFlagsService.getEnableTreeTable() && isTree) {
            width += DEFAULT_TREE_GROUP_LABEL_COLUMN_WIDTH;
        }
        return width - scrollbarWidth;
    }
    getColumnWidth(columnProps) {
        const { type, visible } = columnProps;
        if (!visible || this.isHiddenColumn(columnProps)) {
            return 0;
        }
        const superscriptionBarColumnNumber = this.getColumns().filter(({ type }) => type === ColumnType.SUPERSCRIPTION_BAR).length;
        const isTree = ExperimentalFlagsService.getEnableTreeTable() && Boolean(this.props.isTree);
        const { width: fittedWidth } = getTableColumnWidth(columnProps.width, this.props, superscriptionBarColumnNumber, isTree, this.tableRef);
        const width = {
            [ColumnType.CHECKBOX]: fittedWidth ?? DEFAULT_CHECKBOX_COLUMN_WIDTH,
            [ColumnType.DEFAULT]: fittedWidth ?? RSTABLE_DEFAULT_COLUMN_SIZE,
            [ColumnType.DYNAMIC]: fittedWidth ?? RSTABLE_DEFAULT_COLUMN_SIZE,
            [ColumnType.SUPERSCRIPTION]: fittedWidth ?? RSTABLE_DEFAULT_COLUMN_SIZE,
            [ColumnType.SUPERSCRIPTION_BAR]: DEFAULT_SUPERSCRIPTION_COLUMN_WIDTH,
        };
        return width[type];
    }
    renderActionColumn() {
        const { withActions } = this.props;
        if (!withActions) {
            return null;
        }
        return (React.createElement(Column, { key: "action", width: DEFAULT_ACTION_COLUMN_WIDTH },
            React.createElement(HeaderCell, { className: "c-table__header-row" }),
            React.createElement(Cell, null, rowData => {
                const content = getDynamicContent(rowData, '', 'action', null, this.onDynamicContentRender);
                return (React.createElement("div", { className: "c-table__action" }, content));
            })));
    }
    renderBarColumns() {
        const { barColumns } = this.state.columnGroups;
        return (barColumns
            .filter((column) => { return column.visible && !this.isHiddenColumn(column); })
            .map((column) => {
            return getSuperscriptionBarColumn(column);
        }));
    }
    renderTreeGroupColumn() {
        const { isTree, treeGroupLabelKey = '' } = this.props;
        if (!(ExperimentalFlagsService.getEnableTreeTable() && isTree)) {
            return null;
        }
        return (React.createElement(Column, { key: "tree-group-header", width: DEFAULT_TREE_GROUP_LABEL_COLUMN_WIDTH },
            React.createElement(HeaderCell, { className: "c-table__header-row" }),
            React.createElement(Cell, null, (rowData) => {
                if (!rowData.children?.length || !rowData[treeGroupLabelKey]) {
                    return null;
                }
                const label = MessageService.translate(String(rowData[treeGroupLabelKey]));
                return (React.createElement(Tooltip, { triggers: [TooltipTrigger.ELLIPSIS], value: label },
                    React.createElement("div", { className: "u-truncate" }, label)));
            })));
    }
    renderChevronColumn() {
        const { keyField, isTree, withExpandedIndicator } = this.props;
        const { expandedRows } = this.state;
        if (!withExpandedIndicator || (ExperimentalFlagsService.getEnableTreeTable() && isTree)) {
            return null;
        }
        return (React.createElement(Column, { key: "chevron", width: DEFAULT_TABLE_COLUMN_WIDTH },
            React.createElement(HeaderCell, { className: "c-table__header-row" }),
            React.createElement(Cell, null, (rowData) => {
                const collapsed = expandedRows.includes(String(rowData[keyField]));
                return (React.createElement(CollapseArrow, { collapsed: !collapsed, onClick: () => this.onRowExpandable(rowData) }));
            })));
    }
    renderColumn(column) {
        const { type, width } = column;
        const { sortedColumn } = this.state;
        const superscriptionBarColumnNumber = this.getColumns().filter(({ type }) => type === ColumnType.SUPERSCRIPTION_BAR).length;
        const isTree = ExperimentalFlagsService.getEnableTreeTable() && Boolean(this.props.isTree);
        const { width: fittedWidth } = getTableColumnWidth(width, this.props, superscriptionBarColumnNumber, isTree, this.tableRef);
        /** This is needed because if we override width, the % value (if it exists) will be lost and will cause a bug on column re-renders */
        const fittedColumn = { ...column, fittedWidth };
        const renderers = {
            [ColumnType.CHECKBOX]: () => getCheckboxColumn(fittedColumn),
            [ColumnType.DEFAULT]: () => getDefaultColumn(fittedColumn, this.onResize, sortedColumn),
            [ColumnType.DYNAMIC]: () => getDynamicColumn(fittedColumn, this.onDynamicContentRender),
            [ColumnType.SUPERSCRIPTION]: () => getSuperscriptionColumn(fittedColumn),
            [ColumnType.SUPERSCRIPTION_BAR]: () => getSuperscriptionBarColumn(fittedColumn),
        };
        return renderers[type]();
    }
    renderColumns() {
        const { defaultColumns } = this.state.columnGroups;
        return (defaultColumns
            .filter((column) => { return column.visible && !this.isHiddenColumn(column); })
            .map((column) => {
            return this.renderColumn(column);
        }));
    }
    renderCustomizeColumnsModal() {
        const { keyField } = this.props;
        const { defaultColumns } = this.state.columnGroups;
        const data = defaultColumns.map((column) => ({ id: column.id, value: column.value, visible: column.visible }));
        const columnsSelected = data.filter((column) => (column.visible));
        return (React.createElement(TableCustomizeModal, { data: data, onClose: this.onCloseCustomizeColumn, onSelect: this.onSelectCustomize, onSort: this.onSort, primaryKey: keyField, selected: columnsSelected }));
    }
    renderDataTable(width) {
        const { bordered, cellBordered, data, emptyResultTitleExpression, keyField, isTree, loading } = this.props;
        const { customizing, expandedRows } = this.state;
        if (loading) {
            return null;
        }
        const locale = {
            emptyMessage: emptyResultTitleExpression,
            loading: MessageService.translate(LOADING_MESSAGE),
        };
        return (customizing
            ? this.renderCustomizeColumnsModal()
            : React.createElement(DataTable, { autoHeight: true, bordered: bordered, cellBordered: cellBordered, data: data, expandedRowKeys: expandedRows, headerHeight: 40, hover: true, isTree: ExperimentalFlagsService.getEnableTreeTable() && isTree, locale: locale, minHeight: 250, onExpandChange: (_, data) => this.onRowExpandable(data), onRowClick: (data) => this.onRowClick(data), onSortColumn: (dataKey, direction) => this.onColumnSort(new SortEvent({ direction: direction ?? SortOrder.ASC, name: dataKey })), renderRowExpanded: (data) => this.onRenderExpandedContent(data), rowClassName: (data) => this.getRowClassName(data), rowHeight: 46, rowKey: keyField, virtualized: true, width: width ?? this.getTableWidth() },
                this.renderTreeGroupColumn(),
                this.renderBarColumns(),
                this.renderMultiSelectColumn(),
                this.renderColumns(),
                this.renderChevronColumn(),
                this.renderActionColumn()));
    }
    renderEmptyMessage() {
        const { data, emptyResultSubtitleExpression, emptyResultTitleExpression, loading } = this.props;
        if (data.length || loading) {
            return null;
        }
        return (React.createElement(NotFound, { className: "c-data-table__not-found", subtitleExpression: emptyResultSubtitleExpression, titleExpression: emptyResultTitleExpression }));
    }
    renderLoader() {
        const { loading } = this.props;
        if (!loading) {
            return null;
        }
        return (React.createElement(Loader, { className: "c-table__loader", size: isMobile() ? 32 : 64 }));
    }
    renderMultiSelectColumn() {
        const { keyField, selectionType, data } = this.props;
        const { selectedRows } = this.state;
        if (selectionType !== SelectionType.MULTIPLE) {
            return null;
        }
        return (React.createElement(Column, { key: "multiselec", width: DEFAULT_TABLE_COLUMN_WIDTH },
            React.createElement(HeaderCell, { align: TextAlignment.CENTER, className: "c-table__header-row" },
                React.createElement(TableCheckbox, { onChange: this.onCheckboxColumnChange, value: selectedRows.length == data.length })),
            React.createElement(Cell, { align: TextAlignment.CENTER, dataKey: keyField, width: DEFAULT_TABLE_COLUMN_WIDTH }, (rowData, index) => (React.createElement(TableCheckbox, { name: `data-table-row-multiselect-${index}`, onChange: () => this.onCheckboxRowChange(rowData), value: selectedRows.includes(String(rowData[keyField])) })))));
    }
    render() {
        const { children, data, loading, uuid, width } = this.props;
        const { defaultColumns } = this.state.columnGroups;
        const superscriptionBarColumnNumber = this.getColumns().filter(({ type }) => type === ColumnType.SUPERSCRIPTION_BAR).length;
        const columnsUnits = [];
        let areAllUnitsEqual = true;
        const isTree = ExperimentalFlagsService.getEnableTreeTable() && Boolean(this.props.isTree);
        for (const column of defaultColumns) {
            const { unit } = getTableColumnWidth(column.width, this.props, superscriptionBarColumnNumber, isTree, this.tableRef);
            columnsUnits.push(unit);
            areAllUnitsEqual = unit === columnsUnits[0];
        }
        if (!areAllUnitsEqual && !EditorService.isEditor()) {
            throw Error('[Table]: All table columns must have the same measurement unit.');
        }
        const shouldRenderEmptyMessage = !data.length || !defaultColumns.length;
        const isPercentage = columnsUnits[0] === Unit.PERCENT;
        /** Using shouldRenderEmptyMessage in this condition to avoid the empty message to not being centralized */
        const shouldFitWidth = !isPercentage && !shouldRenderEmptyMessage && !loading;
        const wrapperStyle = {
            width: shouldFitWidth ? 'fit-content' : 'auto',
        };
        return (React.createElement(TableProvider, { ref: this.providerRef },
            React.createElement(Container, { acceptedTypes: TableContainer.acceptedTypes, className: "c-table__wrapper", style: wrapperStyle, uuid: uuid },
                React.createElement("div", { className: this.getClassName(), ref: this.tableRef },
                    children,
                    this.renderLoader(),
                    shouldRenderEmptyMessage
                        ? this.renderEmptyMessage()
                        : this.renderDataTable(width ?? undefined)))));
    }
};
Table.contextType = HeadingContext;
Table.propTypes = {
    bordered: bool,
    cellBordered: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    customizeColumnsExpression: expression,
    data: arrayOf(object).notEnumerable(),
    emptyResultSubtitleExpression: expression,
    emptyResultTitleExpression: expression,
    enableColumnCustomization: bool,
    hiddenColumns: arrayOf(string).notEnumerable(),
    initialExpanded: bool,
    isTree: bool,
    keyField: string,
    loading: bool,
    onCollapse: eventListener,
    onDynamicContentRender: eventListener,
    onNextPage: eventListener,
    onPrevPage: eventListener,
    onSelectItem: eventListener,
    onSort: eventListener,
    onUnselectItem: eventListener,
    selectionType: oneOf(supportedSelectionTypes),
    treeGroupLabelKey: string,
    uuid: string,
    width: number,
    withActions: bool,
    withExpandedIndicator: bool,
};
Table.defaultProps = {
    bordered: false,
    cellBordered: false,
    customizeColumnsExpression: CUSTOMIZE_COLUMN_MESSAGE,
    data: [],
    enableColumnCustomization: true,
    hiddenColumns: [],
    selectionType: SelectionType.NONE,
    withExpandedIndicator: true,
};
Table = Table_1 = __decorate([
    AwesomeComponent({ api: TableAPI, containers: [TableContainer], type: ComponentType.LIST })
], Table);
export { Table, };
//# sourceMappingURL=table.js.map